import React, { useState, useEffect } from 'react';
import { Package, ChevronDown, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Order } from './typres';
import { productService } from '../product/productService';

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

type OrderStatus = Order['status'];

const STATUS_CONFIG: Record<OrderStatus, {
  label: string;
  Icon: React.FC<{ className?: string }>;
  badge: string;
  accent: string;
}> = {
  completed: {
    label: 'Выполнен',
    Icon: CheckCircle2,
    badge: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
    accent: 'bg-emerald-500',
  },
  pending: {
    label: 'В обработке',
    Icon: Clock,
    badge: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    accent: 'bg-amber-500',
  },
  cancelled: {
    label: 'Отменён',
    Icon: XCircle,
    badge: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
    accent: 'bg-red-500',
  },
};

const PREVIEW_COUNT = 4;

const OrderCard = ({ order }: { order: Order }) => {
  const [expanded, setExpanded] = useState(false);
  const [pictures, setPictures] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    order.items.forEach(i => { if (i.picture) init[i.id] = i.picture; });
    return init;
  });

  useEffect(() => {
    const missing = order.items.filter(i => !pictures[i.id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map(i =>
        productService.fetchProduct(i.id)
          .then(r => ({ id: i.id, url: r.product.picture }))
          .catch(() => null)
      )
    ).then(results => {
      const patch: Record<number, string> = {};
      results.forEach(r => { if (r?.url) patch[r.id] = r.url; });
      if (Object.keys(patch).length) setPictures(prev => ({ ...prev, ...patch }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
  const preview = order.items.slice(0, PREVIEW_COUNT);
  const extra = order.items.length - PREVIEW_COUNT;

  return (
    <div className="relative overflow-hidden rounded-[24px] border border-foreground/10 bg-card transition-colors hover:border-foreground/[0.16]">

      {/* Left status accent */}
      <div className={`absolute left-0 top-0 h-full w-[3px] ${cfg.accent}`} />

      {/* Header */}
      <div className="pl-5 pr-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-semibold text-foreground">Заказ #{order.id}</span>
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.badge}`}>
                <cfg.Icon className="h-3 w-3" />
                {cfg.label}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-foreground/35">{fmtDate(order.date)}</p>
          </div>
          <span className="shrink-0 text-[22px] font-bold leading-none text-red-600 dark:text-red-400">
            {fmt(order.total)} <span className="text-base font-semibold">₽</span>
          </span>
        </div>

        {/* Thumbnails row */}
        {order.items.length > 0 && (
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-1.5">
              {preview.map((item, i) => (
                pictures[item.id] ? (
                  <img
                    key={i}
                    src={pictures[item.id]}
                    alt={item.name}
                    className="h-10 w-8 rounded-lg object-cover ring-1 ring-foreground/10"
                  />
                ) : (
                  <div
                    key={i}
                    className="flex h-10 w-8 items-center justify-center rounded-lg border border-foreground/8 bg-foreground/[0.05]"
                  >
                    <Package className="h-3.5 w-3.5 text-foreground/20" />
                  </div>
                )
              ))}
              {extra > 0 && (
                <div className="flex h-10 w-8 items-center justify-center rounded-lg border border-foreground/8 bg-foreground/[0.05] text-[11px] font-medium text-foreground/35">
                  +{extra}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="ml-auto flex items-center gap-1 text-xs text-foreground/35 transition hover:text-foreground/70"
            >
              {expanded ? 'Скрыть' : 'Состав'}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {/* Expanded items */}
      {expanded && (
        <div className="border-t border-foreground/[0.07] px-4 pb-4 pt-3 pl-5">
          <div className="space-y-1.5">
            {order.items.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-[14px] border border-foreground/[0.07] bg-foreground/[0.03] px-3 py-2"
              >
                {pictures[item.id] ? (
                  <img
                    src={pictures[item.id]}
                    alt={item.name}
                    className="h-11 w-9 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg border border-foreground/8 bg-foreground/[0.04]">
                    <Package className="h-4 w-4 text-foreground/20" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground/90">
                    {item.name ?? `Товар #${item.id}`}
                  </p>
                  {item.price != null && (
                    <p className="text-xs text-red-600 dark:text-red-400/80">{fmt(item.price)} ₽</p>
                  )}
                </div>
                {item.quantity > 1 && (
                  <span className="shrink-0 rounded-full border border-foreground/10 bg-foreground/[0.05] px-2 py-0.5 text-[11px] text-foreground/40">
                    ×{item.quantity}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCard;
